import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import type { PriceableItemOrigin } from "elephant-web/gql";
import { useCallback, useState } from "react";
import { defineMessage, useIntl } from "react-intl";
import type { LineItem, PriceList } from "../types";
import { useSetLineItemsV2Mutation } from "./__generated__/useUpdateLineItems.gql";

const ERROR_MESSAGE = defineMessage({
  id: "invoicing.updateLineItems.error.msg",
  defaultMessage: "Failed to update invoice line items",
});

const SUCCESS_MESSAGE = defineMessage({
  id: "invoicing.updateLineItems.success.msg",
  defaultMessage: "Line items updated successfully",
});

const _SET_LINE_ITEMS = gql`
  mutation setLineItemsV2($input: SetInvoiceLineItemsV2Input!) {
    setLineItemsV2(input: $input) {
      _id
      lineItems {
        id
        quantity
        cost
        priceListItem {
          uuid
          name
          priceListItemType
          cost
        }
        disabled
        supplier
      }
      balance {
        total
        paid
        due
        currency
      }
    }
  }
`;

// The mutation identifies each line item by its catalogue reference
// (`origin`/`foreignId`) plus tariff + revenue department. Auto-generated
// rows (registration, dispensed drugs) can reach save carrying only a
// server-resolved `priceListItem` (uuid/tariff/department) with no
// line-item `externalReference` — e.g. a stale missing-price row whose
// price now exists on the list. Fall back to the matching catalogue item
// (by uuid, then name) so those rows stay submittable instead of throwing
// on `externalReference.origin` and killing the whole save. PNE-522.
const resolveCatalogueRef = (
  priceListItem: LineItem["priceListItem"],
  priceList?: PriceList,
) => {
  const items = priceList?.priceListItems ?? [];
  // A single catalogue item supplies every fallback field, so a recovered ref
  // is always internally consistent (never one item's identity with another's
  // tariff). uuid is the identifier; name is only a display label, so trust it
  // as a fallback only when it maps to exactly one item — otherwise we'd risk
  // billing against the wrong catalogue entry.
  const byUuid = priceListItem.uuid
    ? items.find((item) => item.uuid === priceListItem.uuid)
    : undefined;
  const byName = items.filter((item) => item.name === priceListItem.name);
  const match = byUuid ?? (byName.length === 1 ? byName[0] : undefined);
  const externalReference =
    priceListItem.externalReference ?? match?.externalReference;
  return {
    origin: externalReference?.origin,
    foreignId: externalReference?.foreignId,
    activeTariffId: priceListItem.activeTariffId ?? match?.activeTariffId,
    revenueDepartment:
      priceListItem.revenueDepartment ?? match?.revenueDepartment,
  };
};

export const transformLineItems = (
  lineItems: LineItem[],
  priceList?: PriceList,
) =>
  lineItems
    ?.filter(({ priceListItem }) => priceListItem)
    ?.map((lineItem) => ({
      lineItem,
      ref: resolveCatalogueRef(lineItem.priceListItem, priceList),
    }))
    ?.filter(({ lineItem, ref }) => {
      if (ref.origin && ref.foreignId) return true;
      // No line-item externalReference and no catalogue match to recover it:
      // the mutation can't identify this item, so it's omitted rather than
      // sent as invalid input that would fail the whole save. NOTE:
      // setLineItemsV2 is replace-all, so an omitted row is dropped from the
      // invoice server-side — warn so a genuinely unpriceable charge is
      // traceable. In practice the auto-generated rows this targets resolve
      // via uuid/name; only truly orphaned rows hit this branch.
      console.warn(
        `Skipping line item "${lineItem.priceListItem.name}" from save: no catalogue reference could be resolved`,
      );
      return false;
    })
    ?.map(({ lineItem, ref }) => ({
      id: lineItem.id || null,
      priceListItem: {
        origin: ref.origin as PriceableItemOrigin,
        foreignId: ref.foreignId as string,
        activeTariffId: ref.activeTariffId,
        revenueDepartment: ref.revenueDepartment,
      },
      quantity: Number(lineItem.quantity),
      disabled: lineItem.disabled,
      supplier: lineItem.supplier,
    }));

export default function useUpdateLineItems(invoiceId: string) {
  const { formatMessage } = useIntl();
  const [saving, setSaving] = useState<boolean>(false);
  const [setLineItems] = useSetLineItemsV2Mutation({
    onCompleted() {
      setSaving(false);
      toast.success(formatMessage(SUCCESS_MESSAGE), {});
    },
    onError() {
      setSaving(false);
      toast.error(formatMessage(ERROR_MESSAGE), {});
    },
  });

  const updateLineItems = useCallback(
    async (lineItems: LineItem[], priceList?: PriceList) => {
      setSaving(true);
      await setLineItems({
        variables: {
          input: {
            id: invoiceId,
            lineItems: transformLineItems(lineItems, priceList),
          },
        },
      });
    },
    [setLineItems, invoiceId],
  );

  return { updateLineItems, saving };
}
